import { useState } from 'react';
import loadable from '@loadable/component';

import If from '@/components/If';

import useUser from '@/hooks/useUser';
import { useUserAccess } from '@/hooks/useUserAccess';
import { useSummary, useDownloadCSV } from '@/hooks/useCompany';
import { useCompanyShareLink } from '@/hooks/useCompanyShareToken';
import useAddNoteAfterWatchingStock from '@/pages/CompanyReport/hooks/useAddNoteAfterWatchingStock';

import { Wrapper } from './styled';

const CompareAndDownload = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/Overview/components/CompareAndDownload'
    ),
  {
    ssr: false,
  }
);
const AddRemoveCompany = loadable(
  () => import('@/components/AddRemoveCompany')
);
const PlansModal = loadable(
  () => import('@/components/OurPlans/components/PlansModal/PlansModal')
);

const WatchlistAddRemoveCompanyToggle = loadable(
  () => import('@/components/WatchlistAddRemoveCompanyToggle')
);

const AddNoteButton = loadable(
  () => import('@/components/Note/components/AddNoteButton')
);

export function SidebarActions() {
  const summary = useSummary();
  const { data: user } = useUser();
  const { data: userAccess } = useUserAccess();
  const [activeModal, setActiveModal] = useState<
    'none' | 'addRemove' | 'plans'
  >('none');

  const downloadCSV = useDownloadCSV(
    summary.canonicalUrl,
    summary.status === 'success' ? summary.uniqueSymbol : undefined
  );
  function handleFetchCompanyCsvReport() {
    downloadCSV?.mutate();
  }
  const { isSharedCompany } = useCompanyShareLink();
  const shouldHideActionButtons = !user.loggedIn && isSharedCompany;

  const isAddNotesAfterWatchingStockEnabled = useAddNoteAfterWatchingStock({
    companyId: summary.status === 'success' ? summary.companyId : undefined,
  });

  if (summary.status === 'loading') return null;
  if (shouldHideActionButtons) return null;

  return (
    <>
      <Wrapper data-cy-id="watchlist-sidebar">
        <WatchlistAddRemoveCompanyToggle
          companyId={summary.companyId}
          tickerSymbol={summary.tickerSymbol}
        />
        {isAddNotesAfterWatchingStockEnabled && (
          <AddNoteButton
            companyId={summary.companyId}
            uniqueSymbol={summary.tickerSymbol}
          />
        )}
        <CompareAndDownload
          companyId={summary.companyId}
          tickerSymbol={summary.tickerSymbol}
          canonicalUrl={summary.canonicalUrl}
          uniqueSymbol={summary.uniqueSymbol}
          canGetReportPdf={userAccess.canGetCompanyReportPdf}
          canGetReportCsv={userAccess.canGetCompanyReportCsv}
          canAddToPortfolio={true}
          isUserLoggedIn={user.loggedIn}
          onFetchCompanyCsvReport={handleFetchCompanyCsvReport}
        />
      </Wrapper>
      <If
        conditions={{
          addRemove: activeModal === 'addRemove',
          plans: activeModal === 'plans',
        }}
      >
        {{
          addRemove: () => (
            <AddRemoveCompany
              onClose={() => setActiveModal('none')}
              tickerSymbol={summary.tickerSymbol}
              companyId={summary.companyId}
              uniqueSymbol={summary.uniqueSymbol}
            />
          ),
          plans: () => (
            <PlansModal onClose={() => setActiveModal('none')}>
              <p>
                Download Company Report is only available on Unlimited Plan.
              </p>
              <h2>Upgrade to unlock Company Report Download</h2>
            </PlansModal>
          ),
        }}
      </If>
    </>
  );
}
